import { useCallback, useEffect, useMemo, useState } from "react";
import { Phase, TradeToEarnCampaignEpoch } from "@cede/types";
import { useCountdown } from "@/hooks/core/useCountdown";
import { useUserPhaseVolume } from "@/hooks/volumeTracker";
import { getCurrentPhase } from "@/utils/campaign";

type UsePvpPhaseProgressParams = {
  campaignName: string;
  currentEpoch?: TradeToEarnCampaignEpoch;
  isWindowBasedCooldown: boolean;
};

/**
 * Tracks the active PVP phase and notifies consumers when the phase rolls over.
 */
export const usePvpPhaseProgress = ({
  campaignName,
  currentEpoch,
  isWindowBasedCooldown,
}: UsePvpPhaseProgressParams) => {
  const [currentPhase, setCurrentPhase] = useState<Phase | undefined>();

  const updateCurrentPhase = useCallback(() => {
    if (!isWindowBasedCooldown && currentEpoch?.phases) {
      const phase = getCurrentPhase(
        currentEpoch.phases
          .filter((p) => p.start && p.end)
          .map((p) => ({ ...p, start: p.start, end: p.end, maxVolume: p.maxVolume || 0, id: p.id || 0 })),
      );
      if (phase) {
        setCurrentPhase(phase);
      }
    }
  }, [currentEpoch, isWindowBasedCooldown]);

  useEffect(() => {
    updateCurrentPhase();
  }, [updateCurrentPhase]);

  const isPvpCampaign = !isWindowBasedCooldown && !!currentPhase;

  const { userPhaseVolume, refetchUserPhaseVolume } = useUserPhaseVolume(campaignName, currentEpoch, currentPhase);

  const handlePhaseReset = useCallback(() => {
    if (isPvpCampaign) {
      updateCurrentPhase();
      refetchUserPhaseVolume();
    }
  }, [isPvpCampaign, refetchUserPhaseVolume, updateCurrentPhase]);

  const phaseCountdownConfig = useMemo(
    () => ({
      onFinish: handlePhaseReset,
    }),
    [handlePhaseReset],
  );

  const phaseResetTimeCountdown = useCountdown(isPvpCampaign ? currentPhase?.end : undefined, phaseCountdownConfig);

  return {
    isPvpCampaign,
    currentPhase,
    userPhaseVolume,
    phaseResetTimeCountdown,
  };
};
